import React from 'react';
import { Alert as MuiAlert, AlertProps, alertClasses, AlertTitle, styled, ButtonBase, Box, alpha } from '@mui/material';
import { AlertIcon } from './AlertIcons';

export type ToastAlertProps = {
  severity: AlertProps['severity'];
  onClose: AlertProps['onClose'];
  title: AlertProps['title'];
  subtitle?: string;
  action?: {
    label: string;
    onClick: () => void;
  };
};

const Alert = styled(MuiAlert, {
  shouldForwardProp: (prop) => prop !== 'hasSubtitle',
})<{ hasSubtitle: boolean }>(({ theme, hasSubtitle }) => ({
  [`&.${alertClasses.root}`]: {
    borderRadius: '12px',
    border: `1px solid ${theme.palette.grey[300]}`,
    padding: hasSubtitle ? '9px 16px 16px 16px' : '9px 16px',
    boxShadow: '0px 12px 16px -4px rgba(16, 24, 40, 0.08), 0px 4px 6px -2px rgba(16, 24, 40, 0.03)',
    alignItems: hasSubtitle ? 'flex-start' : 'center',
    /**
     * By default we want the icon & action to inherit the color property of the parent
     * as decalred below.
     */
    [`& .${alertClasses.icon}`]: {
      color: 'inherit',
      marginRight: '8px',
      padding: 0,
    },
    [`& .${alertClasses.message}`]: {
      /**
       * The icon has wappers that take up space so we need to add padding to the title when there is a subtitle
       * to make it align with the icon
       */
      paddingTop: hasSubtitle ? '6px' : 0,
      paddingBottom: 0,
      '& .MuiAlertTitle-root': {
        margin: 0,
        fontSize: '14px',
        fontWeight: hasSubtitle ? 600 : 400,
        lineHeight: '20px',
        color: hasSubtitle ? theme.palette.grey[700] : 'inherit',
      },
    },
    [`& .${alertClasses.action}`]: {
      paddingTop: hasSubtitle ? '1px' : 0,
      color: 'inherit',
      alignItems: 'center',
    },
  },
  [`&.${alertClasses.colorInfo}`]: {
    backgroundColor: theme.palette.grey[50],
    color: theme.palette.grey[600],
    border: `1px solid ${theme.palette.grey[300]}`,
  },
  [`&.${alertClasses.colorSuccess}`]: {
    backgroundColor: alpha(theme.palette.success.main, 0.1),
    color: theme.palette.grey[600],
    border: `1px solid ${theme.palette.success.main}`,
  },
  [`&.${alertClasses.colorWarning}`]: {
    backgroundColor: alpha(theme.palette.warning.main, 0.1),
    color: theme.palette.grey[600],
    border: `1px solid ${theme.palette.warning.main}`,
  },
  [`&.${alertClasses.colorError}`]: {
    backgroundColor: alpha(theme.palette.error.main, 0.1),
    color: theme.palette.grey[600],
    border: `1px solid ${theme.palette.error.main}`,
  },
}));

const StyledButton = styled(ButtonBase)(({ theme }) => ({
  color: theme.palette.primary.main,
  fontSize: '14px',
  fontWeight: 600,
  lineHeight: '20px',
}));

const ToastAlertInner = (
  { severity, onClose, title, subtitle, action }: ToastAlertProps,
  ref: React.Ref<HTMLDivElement>,
) => {
  return (
    <Alert
      ref={ref}
      severity={severity}
      onClose={onClose}
      icon={<AlertIcon severity={severity} />}
      hasSubtitle={!!subtitle}
      data-testid={`${severity}-snackbar`}
      title={title}
    >
      {title && <AlertTitle>{title}</AlertTitle>}
      {subtitle}
      {action && (
        <Box marginTop={'10px'}>
          <StyledButton onClick={action.onClick}>{action.label}</StyledButton>
        </Box>
      )}
    </Alert>
  );
};

// Snackbar passes a ref to the Alert component
export const ToastAlert = React.forwardRef(ToastAlertInner);
